import { useForm } from 'react-hook-form';
import { HrisProvider } from '../../../../../enums/IntegrationProvider/HrisProvider';
import React, { useEffect } from 'react';
import { PersonalInformationRows } from './PersonalInformationRows';
import { ProfessionalInformationRows } from './ProfessionalInformationRows';
import { ModalAlertMessage } from './ModalAlertMessage';
import { PushToHrisFormValues } from '..';
import { ExportAndCloseButtons } from './ExportAndCloseButtons';
import { Application } from '../../../../../entities/v1/applicant_tracking/Application';
import { AlertObject, AlertType } from '../../../../../components/Alert';
import { ApplicationService } from '../../../../../services/applicant_tracking/ApplicationService';
import { getHrisName } from '../../../../../utils/hris';
import { getLocationString } from '../../../../../components/LocationSelect';
import { BackgroundCheck } from '../../../../../entities/BackgroundCheck';
import { SurveyRequest } from '../../../../../entities/v1/reference_check/SurveyRequest';
import { HrisSuggestionsService } from '../../../../../services/v1/applicant_tracking/HrisSuggestionsService';
import { HrisSuggestions } from '../../../../../entities/v1/HrisSuggestions';

interface HrisFieldError {
  field: string;
  attempted_value: string | null;
  provider: string;
  message: string;
}

interface PropTypes {
  application: Application;
  applicationIds: number[];
  surveyRequest: SurveyRequest;
  backgroundCheck: BackgroundCheck;
  setAlert: (alert: AlertObject) => void;
  setIsLoading: (isLoading: boolean) => void;
  hrisProvider: HrisProvider;
  onClose: (succeeded: boolean) => void;
}

function getAlertSuccessMessage(hrisProvider: HrisProvider) {
  return `The candidate was successfully exported to ${getHrisName(
    hrisProvider,
  )}!`;
}

function getAlertErrorMessage(hrisProvider: HrisProvider) {
  return `The candidate could not be exported to ${getHrisName(hrisProvider)}!`;
}

function labelForField(field: string): string {
  switch (field) {
    case 'job_title':
      return 'Job Title';
    case 'department':
      return 'Department';
    case 'location':
      return 'Office';
    default:
      return field;
  }
}

export function ModalBody(props: PropTypes) {
  const [firstName, lastName] = props.application.candidate.name.split(' ');
  const [hrisErrors, setHrisErrors] = React.useState<HrisFieldError[]>([]);
  const [suggestions, setSuggestions] = React.useState<HrisSuggestions | null>(
    null,
  );

  const {
    register,
    handleSubmit,
    watch,
    control,
    getValues,
    setValue,
    formState,
    reset,
  } = useForm<PushToHrisFormValues>({
    mode: 'onChange',
    defaultValues: {
      firstName: firstName,
      lastName: lastName,
      personalEmail: props.application.candidate.email,
      workEmail: props.application.candidate.email,
      phoneNumber: props.application.candidate.phoneNumber,
      location: {
        name: getLocationString(props.application.candidate),
        city: props.application.candidate.city,
        country: props.application.candidate.countryCode,
        state: props.application.candidate.stateCode,
      },
      office: {
        value: props.application.jobPost.location.id.toString(),
        label: props.application.jobPost.location.name,
      },
      department: {
        value: props.application.job.department.id.toString(),
        label: props.application.job.department.name,
      },
      jobName: props.application.job.name,
      resume: {
        id: props.application?.applicationResume?.id,
        fileName: props.application?.applicationResume?.id ? 'CV.pdf' : null,
        fileContent: null,
      },
      coverLetter: {
        id: props.application?.applicationCoverLetter?.id,
        fileName: props.application?.applicationCoverLetter?.id
          ? 'cover_letter.pdf'
          : null,
        fileContent: null,
      },
      referenceCheck: {
        id: props.surveyRequest?.partiallyCompleted
          ? props.surveyRequest?.id
          : null,
        fileName: props.surveyRequest?.partiallyCompleted
          ? 'reference_check.pdf'
          : null,
        fileContent: null,
      },
      backgroundCheck: {
        id: props.backgroundCheck?.id,
        fileName: props.backgroundCheck?.id ? 'background_check.pdf' : null,
        fileContent: null,
      },
      startDate: props.application.hiredAt,
    },
  });

  useEffect(() => {
    let cancelled = false;
    HrisSuggestionsService.get(props.application.id)
      .then((response) => {
        if (cancelled) return;
        if (response.jobTitle?.label) {
          setValue('jobName', response.jobTitle.label);
        }
        if (response.department?.value) {
          setValue('department', {
            value: response.department.value,
            label: response.department.label,
          });
        }
        if (response.office?.value) {
          setValue('office', {
            value: response.office.value,
            label: response.office.label,
          });
        }
        setSuggestions(response);
      })
      .catch(() => {
        // On suggestion-fetch failure, treat all three fields as having no
        // recommendation so the warning tooltip fires.
        setSuggestions({ jobTitle: null, department: null, office: null });
      });
    return () => {
      cancelled = true;
    };
  }, [props.application.id, setValue]);

  useEffect(() => {
    const { resume, coverLetter, referenceCheck, backgroundCheck } = watch();

    if (formState.dirtyFields.resume?.fileContent || !resume?.fileName) {
      setValue('resume.id', null);
    }

    if (
      formState.dirtyFields.coverLetter?.fileContent ||
      !coverLetter?.fileName
    ) {
      setValue('coverLetter.id', null);
    }

    if (
      formState.dirtyFields.referenceCheck?.fileContent ||
      !referenceCheck?.fileName
    ) {
      setValue('referenceCheck.id', null);
    }

    if (
      formState.dirtyFields.backgroundCheck?.fileContent ||
      !backgroundCheck?.fileName
    ) {
      setValue('backgroundCheck.id', null);
    }
  }, [
    watch('resume.fileContent'),
    watch('resume.fileName'),
    watch('coverLetter.fileContent'),
    watch('coverLetter.fileName'),
    watch('referenceCheck.fileContent'),
    watch('referenceCheck.fileName'),
    watch('backgroundCheck.fileContent'),
    watch('backgroundCheck.fileName'),
  ]);

  const onPushingToHris = async (
    data: PushToHrisFormValues,
    _e: React.SyntheticEvent,
  ) => {
    try {
      if (props.applicationIds?.length > 0) {
        props.setIsLoading(true);
        setHrisErrors([]);

        await ApplicationService.pushToHris({
          applicationIds: props.applicationIds,
          pushToHrisFormValues: data,
        });

        props.setAlert({
          type: AlertType.Success,
          message: getAlertSuccessMessage(props.hrisProvider),
        });

        reset();
        props.onClose(true);
      }
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errors = (e as any)?.body?.errors as HrisFieldError[] | undefined;
      if (errors && errors.length > 0) {
        setHrisErrors(errors);
        // Modal stays open; user corrects and re-submits.
      } else {
        props.setAlert({
          type: AlertType.Danger,
          message: getAlertErrorMessage(props.hrisProvider),
        });
        props.onClose(false);
      }
    } finally {
      props.setIsLoading(false);
    }
  };

  return (
    <>
      <ModalAlertMessage hrisProvider={props.hrisProvider} />
      {hrisErrors.length > 0 && (
        <div className='alert alert-danger mx-3 mb-0'>
          <strong>Could not export:</strong>
          <ul className='mb-0 ps-3'>
            {hrisErrors.map((err, idx) => (
              <li key={idx}>
                {err.provider} rejected{' '}
                <strong>{labelForField(err.field)}</strong>
                {err.attempted_value ? (
                  <>
                    {' '}
                    value <em>&quot;{err.attempted_value}&quot;</em>
                  </>
                ) : null}
                . Pick an option from the dropdown or enter the exact value as
                it appears in {err.provider}.
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className='app-card-body px-3 w-100'>
        <form onSubmit={handleSubmit(onPushingToHris)}>
          <div className='item mt-4'>
            <PersonalInformationRows
              register={register}
              control={control}
              errors={formState.errors}
            />
            <hr className='mb-4 mt-4' />
            <ProfessionalInformationRows
              register={register}
              control={control}
              getValues={getValues}
              setValue={setValue}
              errors={formState.errors}
              startDate={watch('startDate')}
              hrisProviderLabel={getHrisName(props.hrisProvider)}
              suggestions={suggestions}
            />
          </div>
          <ExportAndCloseButtons
            onClose={props.onClose}
            reset={reset}
            isValid={formState.isValid}
          />
        </form>
      </div>
    </>
  );
}
